import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, ArrowRightLeft, Users, Search, MapPin, ArrowRight, ChevronDown, Plus, Minus, Sparkles, X, CalendarDays, Baby, UserRound, CircleUser, Building2 } from 'lucide-react';
import popularAirportsData from '../data/popular_airports.json';
import { isDomesticRoute } from '../utils/domesticAirports';
import Footer from '../components/Footer';
import BottomNav from '../components/BottomNav';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import HotelSearchForm from '../components/hotel/HotelSearchForm';
import SpecialDeals from '../components/home/SpecialDeals';
import TopAirlines from '../components/home/TopAirlines';
import PopularDestinations from '../components/home/PopularDestinations';
import { useSiteStatus } from '../context/SiteStatusContext';
import PageMeta from '../components/PageMeta';

// Lazy load full airports data only when needed
let fullAirportsData = null;
const loadFullAirports = async () => {
  if (!fullAirportsData) {
    const module = await import('../data/airports.json');
    fullAirportsData = module.default;
  }
  return fullAirportsData;
};

/* ═══════════════════════════════════════════════════
   Airport Picker Dropdown
   ═══════════════════════════════════════════════════ */
const AirportPicker = ({ label, selectedCode, onSelect, side, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [allAirports, setAllAirports] = useState(null);
  const inputRef = useRef(null);
  const ref = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Use popular airports by default, load full list when searching
  const airports = useMemo(() => {
    return allAirports || popularAirportsData;
  }, [allAirports]);
  
  const info = airports.find((a) => a.code === selectedCode);

  // Debounced search with lazy loading
  const handleSearchChange = async (value) => {
    setSearch(value);
    
    // If searching and we don't have full airports yet, load them
    if (value.length > 0 && !allAirports) {
      setIsSearching(true);
      try {
        const fullData = await loadFullAirports();
        setAllAirports(fullData);
      } catch (error) {
        console.error('Failed to load airports:', error);
      } finally {
        setIsSearching(false);
      }
    }
  };

  // Optimized filtering with max results
  const filtered = useMemo(() => {
    if (!search) {
      // Show popular airports when not searching
      return popularAirportsData.slice(0, 50);
    }
    
    const searchLower = search.toLowerCase();
    const results = airports.filter(
      (a) =>
        a.city.toLowerCase().includes(searchLower) ||
        a.code.toLowerCase().includes(searchLower) ||
        a.name.toLowerCase().includes(searchLower)
    );
    
    // Limit results to 100 for performance
    return results.slice(0, 100);
  }, [search, airports]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.15em] mb-1">{label}</p>
      <button
        type="button"
        data-testid={testId}
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="w-full text-left group"
      >
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5">
            <div className="w-7 h-7 rounded-full bg-[#659A9B]/10 flex items-center justify-center group-hover:bg-[#659A9B]/20 transition-colors">
              <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
            </div>
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-extrabold text-[#1B375B] leading-none tracking-tight">{selectedCode}</p>
            <p className="text-xs text-gray-600 font-medium mt-0.5 truncate">{info?.city}, {info?.country}</p>
            <p className="text-[10px] text-gray-400 truncate">{info?.name}</p>
          </div>
        </div>
      </button>

      {open && (
        <div className={`absolute top-full mt-2 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 z-50 overflow-hidden w-[310px] animate-fadeInUp ${side === 'right' ? 'right-0' : 'left-0'}`}>
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="City, airport or code..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#659A9B] focus:bg-white transition-all"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#659A9B]"></div>
                </div>
              )}
            </div>
            {!search && (
              <p className="text-[10px] text-gray-400 mt-1.5">Popular destinations</p>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.map((a) => (
              <button
                key={a.code}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all hover:bg-[#659A9B]/5 ${a.code === selectedCode ? 'bg-[#659A9B]/10' : ''}`}
                onClick={() => { onSelect(a.code); setOpen(false); setSearch(''); }}
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-[#1B375B]">{a.code}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-[#1B375B] truncate">{a.city}, {a.country}</p>
                  <p className="text-[10px] text-gray-400 truncate">{a.name}</p>
                </div>
                {a.code === selectedCode && (
                  <div className="w-5 h-5 rounded-full bg-[#659A9B] flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                )}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-center py-4 text-sm text-gray-400">No airports found</p>}
            {search && filtered.length === 100 && (
              <p className="text-center py-2 text-[10px] text-gray-400 border-t border-gray-100">Showing top 100 results</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   Compact Airport Picker (for Multi City rows)
   ═══════════════════════════════════════════════════ */
const MultiCityAirport = ({ label, code, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [allAirports, setAllAirports] = useState(null);
  const inputRef = useRef(null);
  const ref = useRef(null);
  
  // Use popular airports by default
  const airports = useMemo(() => {
    return allAirports || popularAirportsData;
  }, [allAirports]);
  
  const info = airports.find((a) => a.code === code);

  // Lazy load full airports when searching
  const handleSearchChange = async (value) => {
    setSearch(value);
    
    if (value.length > 0 && !allAirports) {
      setIsSearching(true);
      try {
        const fullData = await loadFullAirports();
        setAllAirports(fullData);
      } catch (error) {
        console.error('Failed to load airports:', error);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const filtered = useMemo(() => {
    if (!search) {
      return popularAirportsData.slice(0, 50);
    }
    
    const searchLower = search.toLowerCase();
    const results = airports.filter(
      (a) =>
        a.city.toLowerCase().includes(searchLower) ||
        a.code.toLowerCase().includes(searchLower) ||
        a.name.toLowerCase().includes(searchLower)
    );
    
    return results.slice(0, 100);
  }, [search, airports]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-[0.12em] mb-0.5">{label}</p>
      <button onClick={() => { setOpen(!open); setSearch(''); }} className="w-full text-left">
        <p className="text-lg font-extrabold text-[#1B375B] leading-tight">{code}</p>
        <p className="text-[10px] text-gray-500 truncate">{info?.city}, {info?.country}</p>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-[0_15px_50px_rgba(0,0,0,0.18)] border border-gray-100 z-50 w-[280px] animate-fadeInUp">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="City or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#659A9B] transition-all"
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.map((a) => (
              <button
                key={a.code}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-all hover:bg-[#659A9B]/5 text-sm ${a.code === code ? 'bg-[#659A9B]/10' : ''}`}
                onClick={() => { onSelect(a.code); setOpen(false); setSearch(''); }}
              >
                <span className="font-bold text-[#1B375B] w-10">{a.code}</span>
                <span className="text-gray-600 truncate">{a.city}, {a.country}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   Calendar Date Picker (uses shadcn)
   ═══════════════════════════════════════════════════ */
const DatePicker = ({ label, date, onSelect, placeholder, minDate, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = date ? new Date(date) : undefined;

  const handleSelect = (day) => {
    if (day) {
      const yyyy = day.getFullYear();
      const mm = String(day.getMonth() + 1).padStart(2, '0');
      const dd = String(day.getDate()).padStart(2, '0');
      onSelect(`${yyyy}-${mm}-${dd}`);
      setOpen(false);
    }
  };

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className="w-full text-left">
          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.15em] mb-1">{label}</p>
          <div className="flex items-start gap-2.5 group">
            <div className="mt-0.5">
              <div className="w-7 h-7 rounded-full bg-[#659A9B]/10 flex items-center justify-center group-hover:bg-[#659A9B]/20 transition-colors">
                <CalendarDays className="w-3.5 h-3.5 text-[#659A9B]" />
              </div>
            </div>
            <div>
              {selected ? (
                <>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-[#1B375B] leading-none">
                      {String(selected.getDate()).padStart(2, '0')}
                    </span>
                    <span className="text-xs font-bold text-[#1B375B]">
                      {format(selected, "MMM''yy")}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">{days[selected.getDay()]}</p>
                </>
              ) : (
                <div>
                  <p className="text-sm font-medium text-[#659A9B]">{placeholder || 'Select'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Tap to pick</p>
                </div>
              )}
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border-0" align="start" sideOffset={8}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={(d) => minDate ? d < new Date(minDate) : d < new Date(new Date().setHours(0,0,0,0))}
          initialFocus
          className="rounded-2xl"
          classNames={{
            day_selected: "bg-[#659A9B] text-white hover:bg-[#335E77] hover:text-white focus:bg-[#335E77] focus:text-white",
            day_today: "bg-[#659A9B]/10 text-[#1B375B] font-bold",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

/* ═══════════════════════════════════════════════════
   Passenger Selector  (ADT / CHD / INF)
   ═══════════════════════════════════════════════════ */
const PassengerSelector = ({ adults, children, infants, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const total = adults + children + infants;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const Counter = ({ label, sublabel, icon, value, onInc, onDec, min = 0, max = 9 }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#659A9B]/10 flex items-center justify-center">
          {icon}
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1B375B]">{label}</p>
          <p className="text-[10px] text-gray-400">{sublabel}</p>
        </div>
      </div>
      <div className="flex items-center gap-2.5">
        <button
          onClick={onDec}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#659A9B] hover:text-[#659A9B] transition-colors text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="text-lg font-bold text-[#1B375B] w-6 text-center">{value}</span>
        <button
          onClick={onInc}
          disabled={value >= max}
          className="w-8 h-8 rounded-full border-2 border-[#659A9B] text-[#659A9B] flex items-center justify-center hover:bg-[#659A9B] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <div ref={ref} className="relative">
      <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.15em] mb-1">Travellers</p>
      <button type="button" data-testid="search-pax-trigger" onClick={() => setOpen(!open)} className="flex items-start gap-2.5 group text-left">
        <div className="mt-0.5">
          <div className="w-7 h-7 rounded-full bg-[#659A9B]/10 flex items-center justify-center group-hover:bg-[#659A9B]/20 transition-colors">
            <Users className="w-3.5 h-3.5 text-[#659A9B]" />
          </div>
        </div>
        <div>
          <p className="text-2xl font-extrabold text-[#1B375B] leading-none">{String(total).padStart(2, '0')}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {adults > 0 && `${adults} ADT`}
            {children > 0 && ` · ${children} CHD`}
            {infants > 0 && ` · ${infants} INF`}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 z-50 w-[300px] animate-fadeInUp">
          <div className="px-5 pt-4 pb-1">
            <p className="text-xs font-bold text-[#1B375B] uppercase tracking-wider mb-1">Passengers</p>
          </div>
          <div className="px-5 divide-y divide-gray-100">
            <Counter
              label="Adults"
              sublabel="12+ years"
              icon={<CircleUser className="w-4 h-4 text-[#659A9B]" />}
              value={adults}
              onInc={() => onChange({ adults: adults + 1, children, infants })}
              onDec={() => onChange({ adults: adults - 1, children, infants })}
              min={1}
              max={9}
            />
            <Counter
              label="Children"
              sublabel="2 – 11 years"
              icon={<UserRound className="w-4 h-4 text-[#659A9B]" />}
              value={children}
              onInc={() => onChange({ adults, children: children + 1, infants })}
              onDec={() => onChange({ adults, children: children - 1, infants })}
              min={0}
              max={6}
            />
            <Counter
              label="Infants"
              sublabel="Under 2 years"
              icon={<Baby className="w-4 h-4 text-[#659A9B]" />}
              value={infants}
              onInc={() => onChange({ adults, children, infants: infants + 1 })}
              onDec={() => onChange({ adults, children, infants: infants - 1 })}
              min={0}
              max={adults}
            />
          </div>
          <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center">
            <p className="text-xs text-gray-400">Infants must be fewer than adults</p>
            <button onClick={() => setOpen(false)} className="px-4 py-1.5 bg-[#1B375B] text-white rounded-lg text-xs font-semibold hover:bg-[#659A9B] transition-colors">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   Sprint-6 · Advanced Search Options (B3/B4/B5)
   ═══════════════════════════════════════════════════ */
const POPULAR_AIRLINE_CODES = [
  { code: '6E', name: 'IndiGo' },
  { code: 'AI', name: 'Air India' },
  { code: 'UK', name: 'Vistara' },
  { code: 'SG', name: 'SpiceJet' },
  { code: 'IX', name: 'Air India Express' },
  { code: 'QR', name: 'Qatar Airways' },
  { code: 'EK', name: 'Emirates' },
  { code: 'EY', name: 'Etihad' },
  { code: 'SQ', name: 'Singapore Airlines' },
  { code: 'TG', name: 'Thai Airways' },
];

const AdvancedSearchOptions = ({
  open,
  onToggle,
  pft, setPft,
  preferredAirlines, setPreferredAirlines,
  stopFilter, setStopFilter,
}) => {
  const togglePreferred = (code) => {
    setPreferredAirlines((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= 10) return prev;  // Tripjack caps at 10
      return [...prev, code];
    });
  };

  return (
    <div className="border-t border-gray-100 pt-2 mt-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-[11px] font-bold text-[#659A9B] uppercase tracking-wider py-1 hover:text-[#1B375B] transition-colors"
        style={{ fontFamily: 'Manrope, sans-serif' }}
        data-testid="advanced-search-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          Advanced options
          {(pft || preferredAirlines.length > 0 || stopFilter !== 'any') && (
            <span className="text-[9px] bg-[#659A9B] text-white px-1.5 py-0.5 rounded-full">
              {[
                pft ? 1 : 0,
                preferredAirlines.length > 0 ? 1 : 0,
                stopFilter !== 'any' ? 1 : 0,
              ].reduce((a, b) => a + b, 0)} active
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className="space-y-3 pt-3 pb-1 animate-fadeIn"
          data-testid="advanced-search-panel"
        >
          {/* Special fare (pft) */}
          <div>
            <p className="text-[10px] font-bold text-[#1B375B] uppercase tracking-wider mb-1.5">
              Special fare
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { value: '', label: 'Regular' },
                { value: 'STUDENT', label: 'Student' },
                { value: 'SENIOR_CITIZEN', label: 'Senior Citizen' },
              ].map((opt) => (
                <button
                  key={opt.value || 'regular'}
                  type="button"
                  onClick={() => setPft(opt.value)}
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition-all ${
                    pft === opt.value
                      ? 'bg-[#1B375B] text-white border-[#1B375B] font-semibold'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#659A9B]'
                  }`}
                  data-testid={`pft-option-${opt.value || 'regular'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred airlines */}
          <div>
            <p className="text-[10px] font-bold text-[#1B375B] uppercase tracking-wider mb-1.5">
              Preferred airlines
              <span className="ml-1 text-[9px] font-normal text-gray-400">
                (max 10 — leave empty for all)
              </span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {POPULAR_AIRLINE_CODES.map((al) => (
                <button
                  key={al.code}
                  type="button"
                  onClick={() => togglePreferred(al.code)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-all ${
                    preferredAirlines.includes(al.code)
                      ? 'bg-[#659A9B] text-white border-[#659A9B] font-semibold'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#659A9B]'
                  }`}
                  data-testid={`preferred-airline-${al.code}`}
                  title={al.name}
                >
                  {al.code}
                </button>
              ))}
            </div>
          </div>

          {/* Stops preference (Tripjack searchModifiers; forwarded via URL) */}
          <div>
            <p className="text-[10px] font-bold text-[#1B375B] uppercase tracking-wider mb-1.5">
              Stops
            </p>
            <div className="flex flex-col gap-1.5">
              {[
                { id: 'any', label: 'All flights' },
                { id: 'direct', label: 'Non-stop only' },
                { id: 'connecting', label: 'Connecting only' },
              ].map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="radio"
                    name="stop-filter"
                    checked={stopFilter === opt.id}
                    onChange={() => setStopFilter(opt.id)}
                    className="w-3.5 h-3.5 text-[#659A9B] border-gray-300 focus:ring-[#659A9B]"
                    data-testid={`stop-filter-${opt.id}`}
                  />
                  <span className="text-[11px] text-gray-700 font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   HOME PAGE
   ═══════════════════════════════════════════════════ */
const HomePage = ({ embedded = false, bare = false, initialFrom, initialTo } = {}) => {
  const navigate = useNavigate();
  const { hotelSearchEnabled } = useSiteStatus();
  // Phase H2 · Top-level vertical switcher (Flight / Hotel). 'flight' keeps
  // every existing search-widget render path intact; 'hotel' swaps in the
  // new HotelSearchForm component below.
  // If the user landed here from /hotels/results "Modify" → URL carries
  // `?vertical=hotel&...prefill params` and we seed the tab accordingly.
  const _initialVertical = (() => {
    if (typeof window === 'undefined') return 'flight';
    const v = new URLSearchParams(window.location.search).get('vertical');
    return v === 'hotel' ? 'hotel' : 'flight';
  })();
  const [vertical, setVertical] = useState(_initialVertical);

  useEffect(() => {
    if (!hotelSearchEnabled && vertical === 'hotel') {
      setVertical('flight');
    }
  }, [hotelSearchEnabled, vertical]);
  const [tripType, setTripType] = useState('oneWay');
  // URL query-string deep-link (from SeoSearchCTA): ?from=BOM&to=DXB.
  // Falls back to the props passed by embedders, then a safe BOM/DXB default.
  const _urlFrom = (typeof window !== 'undefined')
    ? (new URLSearchParams(window.location.search).get('from') || '').toUpperCase()
    : '';
  const _urlTo = (typeof window !== 'undefined')
    ? (new URLSearchParams(window.location.search).get('to') || '').toUpperCase()
    : '';
  const [fromAirport, setFromAirport] = useState(_urlFrom || initialFrom || 'BOM');
  const [toAirport, setToAirport] = useState(_urlTo || initialTo || 'DXB');
  const [departureDate, setDepartureDate] = useState(() => {
    const today = new Date();
    return format(today, 'yyyy-MM-dd');
  });
  const [returnDate, setReturnDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [travelClass, setTravelClass] = useState('Economy');
  const [isSwapping, setIsSwapping] = useState(false);

  // Sprint-6 · Industry-standard form fields (B3/B4/B5). Hidden under an
  // "Advanced ▾" toggle to keep the default form clean. `pft` fans out to
  // both Tripjack (`pft=STUDENT|SENIOR_CITIZEN`) and Riya (FareType=STU|SR).
  // `preferredAirlines` is sent only to Tripjack (Riya filters client-side).
  // `stopFilter` fans out to Tripjack via URL (`direct_only` / `connecting_only`).
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pft, setPft] = useState('');                  // '' | 'STUDENT' | 'SENIOR_CITIZEN'
  const [preferredAirlines, setPreferredAirlines] = useState([]);  // ['6E', 'AI', ...]
  const [stopFilter, setStopFilter] = useState('any'); // 'any' | 'direct' | 'connecting'

  // Multi-city state
  const [multiLegs, setMultiLegs] = useState([
    { from: 'BOM', to: 'DXB', date: '2024-12-05' },
  ]);

  const handleSwapAirports = () => {
    setIsSwapping(true);
    setTimeout(() => {
      const tmp = fromAirport;
      setFromAirport(toAirport);
      setToAirport(tmp);
      setIsSwapping(false);
    }, 300);
  };

  const handleSearch = () => {
    const totalPax = adults + children + infants;

    // Sprint-5 · Domestic detection now uses shared util mirrored to backend
    // INDIAN_AIRPORTS (50 codes). Was 33 codes here → silently routed valid
    // India routes (PAT/DED/IXM/etc.) to Intl RT or empty.
    const _isDomestic = isDomesticRoute(fromAirport, toAirport);

    // Sprint-6 · Append industry-standard fields to every search URL so the
    // result pages can forward them to /search-all without a re-architect.
    const _extra = new URLSearchParams();
    if (pft) _extra.set('pft', pft);
    if (preferredAirlines.length > 0) _extra.set('preferred_airlines', preferredAirlines.join(','));
    if (stopFilter === 'direct') _extra.set('direct_only', '1');
    if (stopFilter === 'connecting') _extra.set('connecting_only', '1');
    const _extraStr = _extra.toString() ? `&${_extra.toString()}` : '';

    if (tripType === 'multiCity') {
      const legs = multiLegs.map((l) => `${l.from}-${l.to}-${l.date}`).join(',');
      navigate(`/multi-city?legs=${legs}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}${_extraStr}`);
    } else if (tripType === 'roundTrip') {
      if (_isDomestic) {
        navigate(`/round-trip?from=${fromAirport}&to=${toAirport}&date=${departureDate}&return=${returnDate}&pax=${totalPax}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}${_extraStr}`);
      } else {
        navigate(`/international-round-trip?from=${fromAirport}&to=${toAirport}&date=${departureDate}&return=${returnDate}&pax=${totalPax}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}${_extraStr}`);
      }
    } else {
      navigate(`/search?from=${fromAirport}&to=${toAirport}&date=${departureDate}&pax=${totalPax}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}&type=${tripType}${_extraStr}`);
    }
  };

  const addMultiCityLeg = () => {
    if (multiLegs.length < 5) {
      const lastLeg = multiLegs[multiLegs.length - 1];
      setMultiLegs([...multiLegs, { from: lastLeg.to, to: '', date: '' }]);
    }
  };

  const removeMultiCityLeg = (idx) => {
    if (multiLegs.length > 1) {
      setMultiLegs(multiLegs.filter((_, i) => i !== idx));
    }
  };

  const updateMultiLeg = (idx, field, value) => {
    const updated = [...multiLegs];
    updated[idx] = { ...updated[idx], [field]: value };
    setMultiLegs(updated);
  };

  const totalPax = adults + children + infants;

  return (
    <div className={bare ? '' : 'min-h-screen travel-bg pb-20 md:pb-0'}>
      <PageMeta
        title="ACF Tourism | International Flights & Visa Assistance"
        description="Book international flights & get visa assistance with ACF Tourism. 23+ years experience, 100K+ tickets issued. Transparent pricing, real support, trusted since 2002."
        canonicalPath="/"
        ogImage="/acf-tourism-og.jpg"
      />
      {/* Hero — NOT overflow-hidden on the outer wrapper, otherwise it clips
          the search form's airport / date / passenger dropdowns when they
          extend below the hero bounds. Background image + gradient now sit
          inside their own clipped layer so the visual stays crisp. */}
      <div className="relative">
        {!bare && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {/* LCP image — rendered as a real <img> (not background-image)
                so the browser's preload scanner discovers it during HTML
                parse and starts downloading immediately. Combined with
                fetchpriority="high" + the <link rel="preload"> in index.html,
                this shaves ~1.5s off LCP on cold loads.
                WebP + q=70 cuts ~50% bytes vs. the default JPEG. */}
            <img
              src="https://images.unsplash.com/photo-1531329818183-bba7e80bfecd?w=1920&h=900&fit=crop&fm=webp&q=70"
              alt=""
              fetchpriority="high"
              decoding="async"
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover object-center"
              data-testid="home-hero-lcp-image"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1B375B]/80 via-[#1B375B]/55 to-[#E7EEEE]" />
          </div>
        )}

        <div className={`relative z-20 max-w-7xl mx-auto ${bare ? 'px-2 sm:px-3 md:px-4 pt-0 pb-2' : 'px-3 sm:px-4 md:px-6 lg:px-8 pt-4 sm:pt-6 md:pt-12 pb-20 md:pb-28'}`}>
          {!bare && (
          <div className="text-center mb-5 sm:mb-6 md:mb-10">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-2 sm:mb-3 md:mb-4">
              <Sparkles className="w-3 md:w-4 h-3 md:h-4 text-[#659A9B]" />
              <span className="text-white/90 text-xs md:text-sm font-medium">Book flights to 100+ destinations worldwide</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-2 md:mb-3 leading-tight px-3 sm:px-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fly From Anywhere,<br /><span className="text-[#659A9B]">To Anywhere</span>
            </h1>
            <p className="text-white/60 text-sm md:text-lg max-w-xl mx-auto px-3 sm:px-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Search and compare flights from hundreds of airlines</p>
          </div>
          )}

          {/* ══════════ SEARCH WIDGET ══════════ */}
          <div className="max-w-6xl mx-auto">
            {/* Phase H2 · Vertical pill switcher (Flight / Hotel) — hidden when admin disables hotel search */}
            {hotelSearchEnabled && (
            <div className="flex justify-center mb-3 sm:mb-4" data-testid="home-vertical-switcher">
              <div className={`inline-flex rounded-full p-1 border ${bare ? 'bg-[#1B375B]/5 border-[#1B375B]/15' : 'bg-white/10 backdrop-blur-md border-white/20'}`}>
                <button
                  type="button"
                  onClick={() => setVertical('flight')}
                  data-testid="home-vertical-flight"
                  className={`flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all ${
                    vertical === 'flight'
                      ? (bare ? 'bg-[#1B375B] text-white shadow-md' : 'bg-white text-[#1B375B] shadow-md')
                      : (bare ? 'text-[#1B375B]/70 hover:text-[#1B375B]' : 'text-white/85 hover:text-white')
                  }`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  <Plane className="w-3.5 h-3.5" />
                  <span>Flights</span>
                </button>
                <button
                  type="button"
                  onClick={() => setVertical('hotel')}
                  data-testid="home-vertical-hotel"
                  className={`flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold transition-all ${
                    vertical === 'hotel'
                      ? (bare ? 'bg-[#1B375B] text-white shadow-md' : 'bg-white text-[#1B375B] shadow-md')
                      : (bare ? 'text-[#1B375B]/70 hover:text-[#1B375B]' : 'text-white/85 hover:text-white')
                  }`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  <Building2 className="w-3.5 h-3.5" />
                  <span>Hotels</span>
                </button>
              </div>
            </div>
            )}

            {hotelSearchEnabled && vertical === 'hotel' ? (
              <>
                <div className="md:hidden">
                  <HotelSearchForm variant="mobile" />
                </div>
                <div className="hidden md:block">
                  <HotelSearchForm variant="desktop" />
                </div>
              </>
            ) : (
              <>
            {/* MOBILE VERSION - Simplified */}
            <div className="md:hidden">
              {/* Tabs - Mobile */}
              <div className="flex gap-2 mb-3">
                {[
                  { id: 'oneWay', label: 'One Way', testId: 'search-trip-oneway' },
                  { id: 'roundTrip', label: 'Round Trip', testId: 'search-trip-roundtrip' },
                ].map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    data-testid={type.testId}
                    onClick={() => setTripType(type.id)}
                    className={`flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all ${
                      tripType === type.id 
                        ? (bare ? 'bg-[#1B375B] text-white shadow-md' : 'bg-white text-[#1B375B] shadow-md')
                        : (bare ? 'bg-[#1B375B]/8 text-[#1B375B]/70' : 'bg-white/20 text-white/80 backdrop-blur-sm')
                    }`}
                    style={{ fontFamily: 'Manrope, sans-serif', minHeight: '44px' }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Search Card - Mobile */}
              <div className="bg-white rounded-2xl shadow-xl p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                {/* From & To */}
                <div className="space-y-2.5 sm:space-y-3">
                  <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3 active:bg-gray-100 transition-colors">
                    <AirportPicker label="From" selectedCode={fromAirport} onSelect={setFromAirport} side="left" testId="search-origin-trigger" />
                  </div>
                  
                  {/* Swap Button */}
                  <div className="flex justify-center -my-1">
                    <button
                      onClick={handleSwapAirports}
                      className="w-10 h-10 rounded-full bg-[#1B375B] text-white flex items-center justify-center shadow-md active:scale-90 transition-transform"
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3 active:bg-gray-100 transition-colors">
                    <AirportPicker label="To" selectedCode={toAirport} onSelect={setToAirport} side="right" testId="search-destination-trigger" />
                  </div>
                </div>

                {/* Date & Passengers Row */}
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3">
                    <DatePicker label="Departure" date={departureDate} onSelect={setDepartureDate} placeholder="Select" testId="search-departure-trigger" />
                  </div>
                  
                  {tripType === 'roundTrip' && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <DatePicker label="Return" date={returnDate} onSelect={setReturnDate} placeholder="Select" minDate={departureDate} testId="search-return-trigger" />
                    </div>
                  )}
                  
                  {tripType === 'oneWay' && (
                    <button
                      onClick={() => setTripType('roundTrip')}
                      className="bg-gray-50 rounded-xl p-3 active:bg-gray-100 transition-colors"
                      style={{ minHeight: '44px' }}
                    >
                      <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-wider mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Return</p>
                      <p className="text-sm font-semibold text-[#659A9B]" style={{ fontFamily: 'Manrope, sans-serif' }}>+ Add</p>
                    </button>
                  )}
                </div>

                {/* Travellers & Class */}
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3">
                    <PassengerSelector
                      adults={adults}
                      children={children}
                      infants={infants}
                      onChange={({ adults: a, children: c, infants: inf }) => { setAdults(a); setChildren(c); setInfants(inf); }}
                    />
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-wider mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Class</p>
                    <select
                      value={travelClass}
                      onChange={(e) => setTravelClass(e.target.value)}
                      className="w-full text-sm font-bold text-[#1B375B] bg-transparent focus:outline-none"
                      style={{ fontFamily: 'Outfit, sans-serif', minHeight: '30px' }}
                    >
                      <option value="Economy">Economy</option>
                      <option value="Premium Economy">Premium</option>
                      <option value="Business">Business</option>
                      <option value="First Class">First</option>
                    </select>
                  </div>
                </div>

                {/* Sprint-6 · Advanced search options (B3/B4/B5/Direct) */}
                <AdvancedSearchOptions
                  open={showAdvanced}
                  onToggle={() => setShowAdvanced((v) => !v)}
                  pft={pft}
                  setPft={setPft}
                  preferredAirlines={preferredAirlines}
                  setPreferredAirlines={setPreferredAirlines}
                  stopFilter={stopFilter}
                  setStopFilter={setStopFilter}
                />

                {/* Search Button - Mobile */}
                <button
                  onClick={handleSearch}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#1B375B] text-white font-bold rounded-xl active:scale-95 transition-transform shadow-lg text-sm sm:text-base"
                  style={{ fontFamily: 'Manrope, sans-serif', minHeight: '48px' }}
                  data-testid="mobile-search-flights-btn"
                >
                  <Search className="w-5 h-5" />
                  Search Flights
                </button>
              </div>
            </div>

            {/* DESKTOP VERSION - Original */}
            <div className="hidden md:block">
            {/* Tabs */}
            <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide">
              {[
                { id: 'oneWay', label: 'One Way' },
                { id: 'roundTrip', label: 'Round Trip' },
              ].map((type, i, arr) => (
                <button
                  key={type.id}
                  onClick={() => setTripType(type.id)}
                  className={`px-4 md:px-7 py-2.5 md:py-3 text-xs md:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${i === 0 ? 'rounded-tl-2xl' : ''} ${i === arr.length - 1 ? 'rounded-tr-2xl' : ''} ${
                    tripType === type.id
                      ? 'bg-white text-[#1B375B] shadow-sm'
                      : (bare ? 'bg-[#1B375B]/8 text-[#1B375B]/70 hover:bg-[#1B375B]/15' : 'bg-white/20 text-white/80 hover:bg-white/30 backdrop-blur-sm')
                  }`}
                  style={{ fontFamily: 'Manrope, sans-serif', minHeight: '44px' }}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {/* Card */}
            <div className="bg-white rounded-b-2xl rounded-tr-2xl shadow-[0_25px_80px_rgba(0,0,0,0.12)]">
              {/* ─── ONE WAY / ROUND TRIP ─── */}
              {(tripType === 'oneWay' || tripType === 'roundTrip') && (
                <div className="p-4 md:p-6 lg:p-7">
                  <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-0">
                    {/* From */}
                    <div className={`flex-1 md:pr-5 md:border-r md:border-gray-100 transition-all duration-300 ${isSwapping ? 'opacity-0 translate-x-4' : ''}`}>
                      <AirportPicker label="From" selectedCode={fromAirport} onSelect={setFromAirport} side="left" testId="search-origin-trigger" />
                    </div>

                    {/* Swap */}
                    <div className="flex md:items-center justify-center md:px-3 relative z-10 order-2 md:order-none -my-2 md:my-0">
                      <button
                        onClick={handleSwapAirports}
                        className={`w-11 h-11 rounded-full bg-[#1B375B] text-white flex items-center justify-center shadow-lg hover:bg-[#659A9B] transition-all duration-500 hover:shadow-[0_8px_25px_rgba(43,181,160,0.4)] ${isSwapping ? 'rotate-180 scale-90' : ''}`}
                        style={{ minHeight: '44px', minWidth: '44px' }}
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                    </div>

                    {/* To */}
                    <div className={`flex-1 md:pl-5 md:pr-5 md:border-r md:border-gray-100 transition-all duration-300 order-3 md:order-none ${isSwapping ? 'opacity-0 -translate-x-4' : ''}`}>
                      <AirportPicker label="To" selectedCode={toAirport} onSelect={setToAirport} side="right" testId="search-destination-trigger" />
                    </div>

                    {/* Departure */}
                    <div className="md:pl-5 md:pr-5 md:border-r md:border-gray-100 md:min-w-[130px] order-4 md:order-none">
                      <DatePicker label="Departure" date={departureDate} onSelect={setDepartureDate} placeholder="Select" testId="search-departure-trigger" />
                    </div>

                    {/* Return (Round Trip) or Add Return (One Way) */}
                    <div className="md:pl-5 md:pr-5 md:border-r md:border-gray-100 md:min-w-[130px] order-5 md:order-none">
                      {tripType === 'roundTrip' ? (
                        <DatePicker label="Return" date={returnDate} onSelect={setReturnDate} placeholder="Select" minDate={departureDate} testId="search-return-trigger" />
                      ) : (
                        <button
                          onClick={() => { setTripType('roundTrip'); }}
                          className="group w-full"
                          style={{ minHeight: '44px' }}
                        >
                          <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.15em] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Return</p>
                          <div className="flex items-start gap-2.5">
                            <div className="mt-0.5">
                              <div className="w-7 h-7 rounded-full border-2 border-dashed border-[#659A9B]/40 flex items-center justify-center group-hover:border-[#659A9B] transition-colors">
                                <Plus className="w-3.5 h-3.5 text-[#659A9B]/50 group-hover:text-[#659A9B] transition-colors" />
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-[#659A9B]" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Return</p>
                              <p className="text-[10px] text-gray-400 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>& Save More</p>
                            </div>
                          </div>
                        </button>
                      )}
                    </div>

                    {/* Travellers */}
                    <div className="md:pl-5 md:pr-5 md:border-r md:border-gray-100 md:min-w-[120px] order-6 md:order-none">
                      <PassengerSelector
                        adults={adults}
                        children={children}
                        infants={infants}
                        onChange={({ adults: a, children: c, infants: inf }) => { setAdults(a); setChildren(c); setInfants(inf); }}
                      />
                    </div>

                    {/* Class */}
                    <div className="md:pl-5 md:min-w-[110px] order-last">
                      <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.15em] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Class</p>
                      <div className="flex items-start gap-2.5">
                        <div className="mt-0.5">
                          <div className="w-7 h-7 rounded-full bg-[#659A9B]/10 flex items-center justify-center">
                            <Sparkles className="w-3.5 h-3.5 text-[#659A9B]" />
                          </div>
                        </div>
                        <div>
                          <select
                            value={travelClass}
                            onChange={(e) => setTravelClass(e.target.value)}
                            className="text-base md:text-lg font-extrabold text-[#1B375B] bg-transparent focus:outline-none cursor-pointer appearance-none leading-tight"
                            style={{ fontFamily: 'Outfit, sans-serif', minHeight: '44px' }}
                          >
                            <option value="Economy">Economy</option>
                            <option value="Premium Economy">Premium</option>
                            <option value="Business">Business</option>
                            <option value="First Class">First</option>
                          </select>
                          <p className="text-[10px] text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>Class</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ─── MULTI CITY ─── */}
              {tripType === 'multiCity' && (
                <div className="p-5 md:p-6">
                  {/* Flight Legs */}
                  <div className="space-y-3 mb-4">
                    {multiLegs.map((leg, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-0 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:border-[#659A9B]/40 transition-colors"
                      >
                        {/* Leg Badge */}
                        <div className="w-14 flex-shrink-0 bg-[#1B375B] self-stretch flex flex-col items-center justify-center">
                          <Plane className="w-3.5 h-3.5 text-[#659A9B] mb-0.5" />
                          <span className="text-[10px] font-bold text-white/80">{idx + 1}</span>
                        </div>

                        {/* From */}
                        <div className="flex-1 px-4 py-3 border-r border-gray-200 min-w-0">
                          <MultiCityAirport
                            label="From"
                            code={leg.from}
                            onSelect={(code) => updateMultiLeg(idx, 'from', code)}
                          />
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center justify-center w-10 flex-shrink-0">
                          <ArrowRight className="w-4 h-4 text-[#659A9B]" />
                        </div>

                        {/* To */}
                        <div className="flex-1 px-4 py-3 border-r border-gray-200 min-w-0">
                          <MultiCityAirport
                            label="To"
                            code={leg.to || 'DEL'}
                            onSelect={(code) => updateMultiLeg(idx, 'to', code)}
                          />
                        </div>

                        {/* Date */}
                        <div className="w-[150px] flex-shrink-0 px-4 py-3 border-r border-gray-200">
                          <DatePicker
                            label="Date"
                            date={leg.date}
                            onSelect={(d) => updateMultiLeg(idx, 'date', d)}
                            placeholder="Pick date"
                            minDate={idx > 0 ? multiLegs[idx - 1].date : undefined}
                          />
                        </div>

                        {/* Remove */}
                        <div className="w-12 flex-shrink-0 flex items-center justify-center">
                          {multiLegs.length > 1 ? (
                            <button
                              onClick={() => removeMultiCityLeg(idx)}
                              className="w-7 h-7 rounded-full text-gray-300 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          ) : (
                            <div className="w-7" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Another City */}
                  {multiLegs.length < 5 && (
                    <button
                      onClick={addMultiCityLeg}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-[#659A9B]/30 text-[#659A9B] text-sm font-semibold hover:border-[#659A9B] hover:bg-[#659A9B]/5 transition-all mb-4 w-full justify-center"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another City (up to {5 - multiLegs.length} more)
                    </button>
                  )}

                  {/* Bottom row: Travellers + Class */}
                  <div className="flex items-center gap-0 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex-1 px-5 py-3 border-r border-gray-200">
                      <PassengerSelector
                        adults={adults}
                        children={children}
                        infants={infants}
                        onChange={({ adults: a, children: c, infants: inf }) => { setAdults(a); setChildren(c); setInfants(inf); }}
                      />
                    </div>
                    <div className="flex-1 px-5 py-3">
                      <p className="text-[10px] font-bold text-[#659A9B] uppercase tracking-[0.15em] mb-1">Class</p>
                      <div className="flex items-start gap-2.5">
                        <div className="mt-0.5">
                          <div className="w-7 h-7 rounded-full bg-[#659A9B]/10 flex items-center justify-center">
                            <Sparkles className="w-3.5 h-3.5 text-[#659A9B]" />
                          </div>
                        </div>
                        <div>
                          <select
                            value={travelClass}
                            onChange={(e) => setTravelClass(e.target.value)}
                            className="text-lg font-extrabold text-[#1B375B] bg-transparent focus:outline-none cursor-pointer appearance-none leading-tight"
                          >
                            <option value="Economy">Economy</option>
                            <option value="Premium Economy">Premium</option>
                            <option value="Business">Business</option>
                            <option value="First Class">First</option>
                          </select>
                          <p className="text-[10px] text-gray-400">Class</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sprint-6 · Advanced search options (desktop) */}
              <div className="px-4 md:px-6 lg:px-7 pt-3 md:pt-4">
                <AdvancedSearchOptions
                  open={showAdvanced}
                  onToggle={() => setShowAdvanced((v) => !v)}
                  pft={pft}
                  setPft={setPft}
                  preferredAirlines={preferredAirlines}
                  setPreferredAirlines={setPreferredAirlines}
                  stopFilter={stopFilter}
                  setStopFilter={setStopFilter}
                />
              </div>

              {/* Bottom Bar */}
              <div className="border-t border-gray-100 px-4 md:px-6 lg:px-7 py-3 md:py-4 flex items-center justify-end bg-gray-50/50 rounded-b-2xl">
                <button
                  onClick={handleSearch}
                  className="group flex items-center justify-center gap-2 md:gap-3 w-full md:w-auto px-6 md:px-10 py-3 md:py-3.5 bg-[#1B375B] text-white font-bold rounded-xl hover:bg-[#659A9B] transition-all duration-300 shadow-lg hover:shadow-[0_10px_30px_rgba(43,181,160,0.35)] text-sm md:text-base"
                  style={{ fontFamily: 'Manrope, sans-serif', minHeight: '48px' }}
                  data-testid="desktop-search-flights-btn"
                >
                  <Search className="w-4 md:w-5 h-4 md:h-5 group-hover:rotate-12 transition-transform" />
                  Search Flights
                  <ArrowRight className="w-3 md:w-4 h-3 md:h-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                </button>
              </div>
            </div>
            </div>{/* End Desktop Version */}
              </>
            )}{/* End vertical === 'flight' fragment */}

            {/* Trust badges */}
            {!bare && (
            <div className="hidden md:flex items-center justify-center gap-8 mt-8">
              {['100+ Airlines', 'Best Price Guarantee', 'Free Cancellation', '24/7 Support'].map((badge) => (
                <div key={badge} className="flex items-center gap-2 text-white/60">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#659A9B]" />
                  <span className="text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{badge}</span>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      </div>

      {!embedded && <SpecialDeals />}
      {!embedded && <TopAirlines />}
      {!embedded && <PopularDestinations />}
      {!embedded && <Footer />}
      {!embedded && <BottomNav />}
    </div>
  );
};

export default HomePage;
